import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import type {
  Season,
  SeasonData,
  CreateGameResultPayload,
  CreateGameResultResponse,
  AdminUserListItem,
  FullGameRecord,
  UpdateGameResultPayload,
  RenamePlayerPayload,
  RenamePlayerResponse,
} from '../types/scoreboard';

export function useSeasons() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: () => api.get<Season[]>('/scoreboard/seasons'),
  });
}

export function useSeason(seasonId: string) {
  return useQuery({
    queryKey: ['season', seasonId],
    queryFn: () => api.get<SeasonData>(`/scoreboard/seasons/${seasonId}`),
    enabled: !!seasonId,
  });
}

export function useCreateGameResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateGameResultPayload) =>
      api.post<CreateGameResultResponse>('/scoreboard/games', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['seasons'] });
      queryClient.invalidateQueries({ queryKey: ['season', result.seasonId] });
    },
  });
}

export function useGame(seasonId: string, gameId: string) {
  return useQuery({
    queryKey: ['game', seasonId, gameId],
    queryFn: () => api.get<FullGameRecord>(`/scoreboard/games/${seasonId}/${gameId}`),
    enabled: !!seasonId && !!gameId,
  });
}

export function useUpdateGameResult(seasonId: string, gameId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateGameResultPayload) =>
      api.put<CreateGameResultResponse>(`/scoreboard/games/${seasonId}/${gameId}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seasons'] });
      queryClient.invalidateQueries({ queryKey: ['season', seasonId] });
      queryClient.invalidateQueries({ queryKey: ['game', seasonId, gameId] });
    },
  });
}

export function useRenamePlayer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RenamePlayerPayload) =>
      api.post<RenamePlayerResponse>('/scoreboard/players/rename', payload),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['season', variables.seasonId] });
    },
  });
}

export function useAdminUsersList() {
  const { user } = useAuthStore();
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<AdminUserListItem[]>('/users'),
    enabled: isFreeAdmin(user?.email),
  });
}
